import { IdPathParameters, SaveOperation } from '@infuse/api/common';
import { AddPermissionsAction } from '@infuse/iam/role/type';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

export const useGenericAddPermissionToRole = ({
  apiFn,
  forcedParentId
}: {
  apiFn: SaveOperation<AddPermissionsAction, never> | SaveOperation<AddPermissionsAction, IdPathParameters<string>>;
  forcedParentId?: string;
}) => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation<unknown, unknown, AddPermissionsAction & { parentId?: string; roleId: string }>({
    mutationFn: async ({ parentId, roleId, permissionIds, replace = true }) => {
      if (forcedParentId) {
        parentId = forcedParentId;
      }
      try {
        await apiFn(parentId ? { id: parentId, roleId } : { roleId }, { replace, permissionIds });
        notify({
          title: t('iam.roles.header.add_permissions'),
          message: t('iam.roles.notify.add_permissions_success')
        });
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.roles.header.add_permissions'),
          message: getAxiosErrorMessage(e, t('iam.roles.notify.add_permissions_failed'))
        });
        throw e;
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['role-permissions'] });
    }
  });

  return {
    ...mutation
  };
};
